import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Cabine, PortaGrande } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import Carregando from "../componentes/Carregando";
import LeitorAnalise from "./LeitorAnalise";
import LeitorDemonstracao from "./LeitorDemonstracao";
import {
  analisar,
  apagarAnalise,
  criarAnalise,
  lerPdf,
  minhasAnalises,
  type ArquivoLido,
  type ResumoDeAnalise,
} from "../lib/leitorDeProcessos";
import {
  comMilhar,
  cotaDoEnvio,
  MAXIMO_DE_ARQUIVOS,
  NOME_DO_TIPO,
  paginasEmTexto,
  TETO_DE_TEXTO,
  type TipoPericia,
  detectarTipo,
} from "../lib/processos";

// O Leitor Inteligente de Processos (000171) — porte do app da Lovable.
//
// Três telas, despachadas pelo segmento depois do slug:
//
//   /ferramenta/leitor-de-processos          o painel: as análises do perito
//   /ferramenta/leitor-de-processos/nova     o envio: PDFs + tipo de perícia
//   /ferramenta/leitor-de-processos/<uuid>   o resultado, com abas e conversa
//
// `nova` é segmento RESERVADO. Não colide com nada: o outro segmento possível é
// um uuid, e uuid não é palavra.
//
// ---------------------------------------------------------------------------
// O que a origem tinha e aqui NÃO tem, de propósito
// ---------------------------------------------------------------------------
// * **O portão de e-mail.** Lá o aluno digitava o e-mail, o app perguntava ao
//   Bubble (`wf/busca-aluno-pericia`) e guardava a resposta no `localStorage`.
//   Aqui ele já entrou no hub, e quem decide é `abrir_card`.
// * **A tela de "processando" com barra falsa.** A origem sobe 1% a cada 200ms
//   até 95% e troca de frase a cada 3s — não mede nada. Widget alimentado por
//   dado que não existe é o que a doutrina desta casa proíbe desde a primeira
//   vitrine. Aqui a espera mostra o que está REALMENTE acontecendo: qual arquivo
//   está sendo lido, e em que página.
//
// E `exemplo` é o segundo segmento RESERVADO: a demonstração que Victor pediu em
// 02/09, com um processo inventado e a marca de exemplo em toda tela — o cuidado
// que a doutrina do Simulador MEI exige de tela que imita o real. O porquê de
// cada peça dela está em `LeitorDemonstracao.tsx`.
export default function LeitorDeProcessos() {
  const { "*": resto = "" } = useParams();
  const primeiro = resto.split("/").filter(Boolean)[0] ?? null;

  // O container é AQUI, num lugar só — e ele é obrigatório: a `Moldura` de
  // `Ferramenta.tsx` não tem largura própria, cada ferramenta põe a sua (é o que
  // `HubDp`, `ForoFacil` e as outras fazem). A primeira versão desta tela saiu
  // sem, e a captura mostrou o conteúdo encostando nas duas bordas de 1.280px
  // enquanto o Rodapé — que tem `max-w-6xl` na Moldura — ficava centrado: a
  // página parecia ter dois alinhamentos, que é a cicatriz do "Rodape solto no
  // flex" pelo lado inverso.
  //
  // Envolver no DESPACHO, e não em cada tela, faz as três (painel, envio,
  // resultado) e os estados de erro herdarem a mesma largura sem repetição.
  //
  // O `pb` grande nas telas com o BALÃO do assistente (03/09): o botão é
  // `fixed` no canto inferior direito, então ele não empurra nada e passaria por
  // cima do último conteúdo e do Rodapé. Não é margem de gosto — é o espaço que
  // o botão ocupa, e sem ele a última linha da tela fica atrás de um botão
  // opaco. As telas sem balão (painel e envio) não pagam esse respiro.
  const temBalao = primeiro !== null && primeiro !== "nova";

  return (
    <main
      className={`mx-auto w-full max-w-6xl px-4 sm:px-6 ${
        temBalao ? "pt-7 pb-24 sm:pb-20" : "py-7"
      }`}
    >
      {primeiro === "nova" ? (
        <Envio />
      ) : primeiro === "exemplo" ? (
        <LeitorDemonstracao />
      ) : primeiro ? (
        <LeitorAnalise id={primeiro} />
      ) : (
        <Painel />
      )}
    </main>
  );
}

// ===========================================================================
// O painel — as análises do perito
// ===========================================================================
function Painel() {
  const [analises, setAnalises] = useState<ResumoDeAnalise[] | null>(null);
  const [erro, setErro] = useState(false);
  const [busca] = useSearchParams();
  const curso = busca.get("curso");
  const q = curso ? `?curso=${encodeURIComponent(curso)}` : "";

  const recarregar = useCallback(() => {
    void minhasAnalises()
      .then((l) => setAnalises(l))
      .catch(() => setErro(true));
  }, []);

  useEffect(recarregar, [recarregar]);

  if (erro) {
    return (
      <p className="rounded-cf border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos carregar as suas análises agora. Recarregue a página.
      </p>
    );
  }
  if (!analises) return <Carregando />;

  const emAndamento = analises.filter((a) => a.situacao === "em_analise").length;
  const totalDeItens = analises.reduce((s, a) => s + a.itens, 0);
  const feitos = analises.reduce((s, a) => s + a.feitos, 0);

  return (
    <>
      <Cabine
        rotulo="Ferramenta"
        titulo="Leitor Inteligente de Processos"
        descricao={
          <>
            Envie os PDFs de um processo e receba o relatório técnico, a linha do tempo,
            o checklist do perito e os alertas — cada item com a página de onde saiu.
            Depois, converse com o assistente sobre aquele processo.
          </>
        }
        acao={
          <div className="flex flex-wrap items-center gap-3">
            <Link to={`/ferramenta/leitor-de-processos/nova${q}`} className="pill-cta inline-flex">
              Analisar um processo
            </Link>
            {/* A demonstração fica ao lado do CTA, e não escondida: o perito que
                nunca usou a ferramenta quer ver o que ela devolve ANTES de subir
                o processo de um cliente. Sobre navy o link é `laranja-texto` —
                cor semântica aqui viraria texto escuro sobre azul-escuro, que é
                a cicatriz dos cards em escada do login. */}
            <Link
              to={`/ferramenta/leitor-de-processos/exemplo${q}`}
              className="text-sm font-semibold text-laranja-texto underline decoration-laranja-texto/40 hover:decoration-laranja-texto"
            >
              Ver uma demonstração
            </Link>
          </div>
        }
        fatos={
          // Fato só existe com dado medido: carteira vazia, painel sem fato.
          // Zero grande é ruído — a doutrina da Cabine.
          analises.length > 0 ? (
            <>
              <TileDeFato
                rotulo="Processos analisados"
                valor={comMilhar(analises.length)}
                apoio={emAndamento > 0 ? `${emAndamento} em análise agora` : undefined}
              />
              {totalDeItens > 0 && (
                <TileDeFato
                  rotulo="Checklist"
                  valor={`${comMilhar(feitos)} de ${comMilhar(totalDeItens)}`}
                  apoio="itens que você marcou"
                />
              )}
            </>
          ) : undefined
        }
      />

      {analises.length === 0 ? (
        <section className="mt-6 grid gap-4 sm:grid-cols-2">
          <PortaGrande
            para={`/ferramenta/leitor-de-processos/nova${q}`}
            titulo="Analisar o primeiro processo"
            descricao="Você envia os PDFs, a ferramenta lê o texto no seu próprio navegador e devolve o relatório em cerca de um minuto."
            cta="Enviar PDFs"
          />
          <article className="surface rounded-cf p-5">
            <h3 className="font-display text-lg font-bold text-titulo">O que você recebe</h3>
            <ul className="mt-3 grid gap-2 text-sm text-tinta">
              <li>Identificação do processo, objeto da ação e objeto da perícia.</li>
              <li>Pontos controvertidos, quesitos e documentos relevantes.</li>
              <li>Linha do tempo dos atos processuais, em ordem.</li>
              <li>Checklist do perito — e o que você marcar fica salvo aqui.</li>
              <li>Alertas: documento ausente, valor divergente, prazo.</li>
            </ul>
            <p className="mt-3 text-xs text-cinza">
              O PDF não é enviado para os nossos servidores: o texto é extraído no seu
              navegador, e é ele que fica guardado para o assistente poder citar as páginas.
            </p>
          </article>
        </section>
      ) : (
        <section className="mt-6" aria-labelledby="titulo-analises">
          <h2 id="titulo-analises" className="font-display text-xl font-bold text-titulo">
            Os seus processos
          </h2>
          <ul className="mt-3 grid gap-3">
            {analises.map((a) => (
              <LinhaDeAnalise key={a.id} a={a} q={q} aoApagar={recarregar} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function LinhaDeAnalise({
  a,
  q,
  aoApagar,
}: {
  a: ResumoDeAnalise;
  q: string;
  aoApagar: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [apagando, setApagando] = useState(false);

  const quando = new Date(a.criado_em).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <li className="surface rounded-cf p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/ferramenta/leitor-de-processos/${a.id}${q}`}
            className="font-display text-base font-bold text-link hover:underline"
          >
            {a.titulo}
          </Link>
          <p className="mt-1 text-xs text-cinza">
            {NOME_DO_TIPO[a.tipo] ?? a.tipo} · {quando} · {a.paginas} página
            {a.paginas === 1 ? "" : "s"} em {a.arquivos} arquivo{a.arquivos === 1 ? "" : "s"}
          </p>
          {a.situacao === "ok" && a.itens > 0 && (
            <p className="mt-1 text-xs text-cinza">
              Checklist: {a.feitos} de {a.itens} marcado{a.feitos === 1 ? "" : "s"}
            </p>
          )}
          {/* O corte aparece na LISTA, e não só dentro da análise: é aqui que o
              perito escolhe qual processo abrir, e "lido até a página 65 de 98"
              é o que faz ele lembrar de mandar o resto. */}
          {a.situacao === "ok" && a.paginas_lidas !== null && a.paginas_lidas < a.paginas && (
            <p className="mt-1 text-xs text-alerta-tinta">
              Lido até a página {a.paginas_lidas} de {a.paginas} — o resto não foi analisado
            </p>
          )}
          {a.situacao === "em_analise" && (
            <p className="mt-1 text-xs text-alerta-tinta">
              A análise ficou pela metade. Abra para tentar de novo — o texto do processo
              continua salvo.
            </p>
          )}
          {a.situacao === "falha" && (
            <p className="mt-1 text-xs text-erro-tinta">
              A análise não foi concluída. Abra para ver o motivo e tentar de novo.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to={`/ferramenta/leitor-de-processos/${a.id}${q}`}
            className="rounded-full border-[1.5px] border-borda px-4 py-1.5 text-sm font-medium text-tinta transition hover:border-laranja"
          >
            Abrir
          </Link>
          {!confirmando ? (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              className="rounded-cf px-2 py-1 text-xs text-cinza hover:text-erro-tinta"
            >
              Apagar
            </button>
          ) : (
            <span className="flex items-center gap-1 text-xs">
              <button
                type="button"
                disabled={apagando}
                onClick={() => {
                  setApagando(true);
                  void apagarAnalise(a.id).then((ok) => {
                    setApagando(false);
                    if (ok) aoApagar();
                    else setConfirmando(false);
                  });
                }}
                className="rounded-cf bg-erro-fundo px-2 py-1 font-semibold text-erro-tinta"
              >
                {apagando ? "Apagando…" : "Apagar mesmo"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="rounded-cf px-2 py-1 text-cinza"
              >
                Não
              </button>
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

// ===========================================================================
// O envio
// ===========================================================================
type NaFila = {
  chave: string;
  nome: string;
  tamanho: number;
  /** null enquanto lê. */
  lido: ArquivoLido | null;
  /** A página em que a leitura está, para a espera dizer a verdade. */
  progresso: { pagina: number; de: number } | null;
  falha: string | null;
};

function Envio() {
  const navegar = useNavigate();
  const [busca] = useSearchParams();
  const curso = busca.get("curso");
  const q = curso ? `?curso=${encodeURIComponent(curso)}` : "";

  const [fila, setFila] = useState<NaFila[]>([]);
  const [tipo, setTipo] = useState<TipoPericia | "">("");
  const [deteccao, setDeteccao] = useState<{ tipo: TipoPericia | null; empate: boolean } | null>(null);
  const [tipoEscolhidoAMao, setTipoEscolhidoAMao] = useState(false);
  const [enviando, setEnviando] = useState<null | "criando" | "analisando">(null);
  const [erroDoEnvio, setErroDoEnvio] = useState<string | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const lidos = fila.filter((f) => f.lido).map((f) => f.lido!) as ArquivoLido[];
  const lendo = fila.some((f) => !f.lido && !f.falha);
  const cota = cotaDoEnvio(lidos.map((l) => l.texto));

  /** Lê os PDFs escolhidos, um por vez, mostrando em qual página está. */
  const receber = useCallback(
    async (arquivos: File[]) => {
      const pdfs = arquivos.filter(
        (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name),
      );
      if (pdfs.length === 0) return;

      setErroDoEnvio(null);

      for (const arquivo of pdfs) {
        // O teto é do BANCO (`lp_criar_analise` recusa acima de 10), e a tela
        // diz antes em vez de deixar o servidor recusar depois de o perito
        // esperar a leitura de todos.
        let cheio = false;
        setFila((atual) => {
          if (atual.length >= MAXIMO_DE_ARQUIVOS) {
            cheio = true;
            return atual;
          }
          return atual;
        });
        if (cheio) {
          setErroDoEnvio(
            `Uma análise aceita até ${MAXIMO_DE_ARQUIVOS} arquivos. Os arquivos a mais não foram lidos.`,
          );
          break;
        }

        const chave = `${arquivo.name}-${arquivo.size}-${Date.now()}-${Math.random()}`;
        setFila((atual) => [
          ...atual,
          { chave, nome: arquivo.name, tamanho: arquivo.size, lido: null, progresso: null, falha: null },
        ]);

        const r = await lerPdf(arquivo, (pagina, de) => {
          setFila((atual) =>
            atual.map((f) => (f.chave === chave ? { ...f, progresso: { pagina, de } } : f)),
          );
        });

        setFila((atual) =>
          atual.map((f) =>
            f.chave === chave
              ? r.ok
                ? { ...f, lido: r.lido, progresso: null }
                : { ...f, falha: r.mensagem, progresso: null }
              : f,
          ),
        );
      }
    },
    [],
  );

  // A detecção do tipo roda sobre TODO o texto lido até agora, e não sobre o
  // último arquivo: o despacho que nomeia a perícia costuma ser o segundo PDF.
  useEffect(() => {
    if (lidos.length === 0) {
      setDeteccao(null);
      if (!tipoEscolhidoAMao) setTipo("");
      return;
    }
    const d = detectarTipo(lidos.map((l) => l.texto).join("\n\n"));
    setDeteccao({ tipo: d.tipo, empate: d.empate });
    if (d.tipo && !tipoEscolhidoAMao) setTipo(d.tipo);
    // `lidos` é derivado de `fila` a cada render; a dependência é o texto.
  }, [fila.map((f) => f.lido?.caracteres ?? 0).join(","), tipoEscolhidoAMao]);

  const podeEnviar = lidos.length > 0 && !lendo && tipo !== "" && enviando === null;

  async function enviar() {
    // `podeEnviar` já inclui `tipo !== ""`, e o TypeScript estreita `tipo` por
    // aqui (narrowing por condição nomeada). Uma segunda checagem seria código
    // que o compilador prova ser morto — e ele reclama disso, com razão.
    if (!podeEnviar) return;
    setEnviando("criando");
    setErroDoEnvio(null);

    const criacao = await criarAnalise(lidos, tipo);
    if (!criacao.ok) {
      setEnviando(null);
      setErroDoEnvio(criacao.mensagem);
      return;
    }

    // A análise já existe e tem dono. Se o modelo falhar daqui para frente, ela
    // fica em `em_analise` e a tela do resultado oferece "analisar de novo" —
    // sem pedir os PDFs outra vez.
    //
    // DISPARA E NAVEGA, sem `await` (03/09). A análise leva de 60 a 85 segundos
    // MEDIDOS, e esperar aqui deixava o perito um minuto e meio numa tela de
    // envio que não diz nada — enquanto a tela do resultado tem o título, os
    // arquivos, as páginas e agora a espera com o número. O `await` também não
    // servia para nada: o desfecho era ignorado e a navegação acontecia de todo
    // modo.
    //
    // A promise não é cancelada pela desmontagem do componente (só um
    // `AbortController` faria isso, e não há), então a chamada segue viva.
    setEnviando("analisando");
    void analisar(criacao.id);
    navegar(`/ferramenta/leitor-de-processos/${criacao.id}${q}`);
  }

  return (
    <>
      <Cabine
        trilha={{ para: `/ferramenta/leitor-de-processos${q}`, rotulo: "Leitor de Processos" }}
        rotulo="Novo processo"
        titulo="Enviar os PDFs do processo"
        descricao={
          <>
            O texto é extraído aqui no seu navegador — o arquivo não sai do seu computador.
            Você pode enviar até {MAXIMO_DE_ARQUIVOS} arquivos do mesmo processo.
          </>
        }
      />

      <section className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div className="surface rounded-cf p-5">
          <h2 className="font-display text-lg font-bold text-titulo">Arquivos</h2>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void receber(Array.from(e.dataTransfer.files));
            }}
            className="mt-3 rounded-cf border-2 border-dashed border-borda p-6 text-center"
          >
            <input
              ref={entrada}
              id="pdfs-do-processo"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="sr-only"
              onChange={(e) => {
                void receber(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
            <p className="text-sm text-tinta">
              Arraste os PDFs aqui, ou{" "}
              <button
                type="button"
                onClick={() => entrada.current?.click()}
                className="font-semibold text-link underline"
              >
                escolha os arquivos
              </button>
              .
            </p>
            <p className="mt-1 text-xs text-cinza">
              Só PDF, e o PDF precisa ter texto — a digitalização de um papel é imagem, e
              não há texto para extrair.
            </p>
          </div>

          {fila.length > 0 && (
            <ul className="mt-4 grid gap-2">
              {fila.map((f) => (
                <li key={f.chave} className="rounded-cf border border-borda p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-tinta" title={f.nome}>
                      {f.nome}
                    </span>
                    {f.lido ? (
                      <span className="text-xs text-cinza">
                        {f.lido.paginas} página{f.lido.paginas === 1 ? "" : "s"} ·{" "}
                        {comMilhar(f.lido.caracteres)} caracteres
                      </span>
                    ) : f.falha ? (
                      <span className="text-xs font-semibold text-erro-tinta">não deu para ler</span>
                    ) : (
                      <span className="text-xs text-cinza">
                        {/* A espera diz a verdade: qual página está sendo lida. A
                            origem mostra uma barra que sobe sozinha até 95%. */}
                        {f.progresso
                          ? `lendo a página ${f.progresso.pagina} de ${f.progresso.de}…`
                          : "abrindo o arquivo…"}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setFila((atual) => atual.filter((x) => x.chave !== f.chave))}
                      className="rounded-cf px-2 py-0.5 text-xs text-cinza hover:text-erro-tinta"
                    >
                      Tirar
                    </button>
                  </div>
                  {f.falha && <p className="mt-1 text-xs text-erro-tinta">{f.falha}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid gap-4">
          <div className="surface rounded-cf p-5">
            <h2 className="font-display text-lg font-bold text-titulo">Tipo de perícia</h2>
            <p className="mt-1 text-xs text-cinza">
              A ferramenta tenta descobrir pelo texto, e você pode trocar.
            </p>

            <div className="mt-3 grid gap-2">
              {(Object.keys(NOME_DO_TIPO) as TipoPericia[]).map((t) => (
                <label
                  key={t}
                  className={`flex cursor-pointer items-center gap-2 rounded-cf border p-2.5 text-sm ${
                    tipo === t ? "border-azul bg-chip" : "border-borda"
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo-de-pericia"
                    value={t}
                    checked={tipo === t}
                    onChange={() => {
                      setTipo(t);
                      setTipoEscolhidoAMao(true);
                    }}
                    className="accent-azul"
                  />
                  <span className="text-tinta">{NOME_DO_TIPO[t]}</span>
                  {deteccao?.tipo === t && !tipoEscolhidoAMao && (
                    <span className="ml-auto rounded-full bg-chip px-2 py-0.5 text-[0.6875rem] font-semibold text-link">
                      detectado no texto
                    </span>
                  )}
                </label>
              ))}
            </div>

            {deteccao && !deteccao.tipo && lidos.length > 0 && (
              <p className="mt-3 text-xs text-alerta-tinta">
                {deteccao.empate
                  ? "O texto tem sinais de mais de um tipo de perícia em igual medida. Escolha você."
                  : "Não achei no texto as palavras que identificam o tipo de perícia. Escolha você."}
              </p>
            )}
          </div>

          {lidos.length > 0 && (
            <div className="surface rounded-cf p-5">
              <h2 className="font-display text-lg font-bold text-titulo">O que a IA vai ler</h2>
              {/* O AVISO que a origem não tem. Ela corta em 120.000 caracteres e
                  não conta a ninguém — o perito recebe um relatório com cara de
                  completo sobre o primeiro terço de um processo grande.

                  Desde 03/09/2026 o hub lê 1.000.000 (cerca de 565 páginas), o
                  que faz este aviso ser raro: ele era a tela COMUM da Perícia e
                  passou a ser o caso do processo de mais de meio milhar de
                  páginas. O aviso fica de todo modo, porque o corte não
                  desapareceu — só recuou. */}
              {cota.cabe ? (
                <p className="mt-2 text-sm text-tinta">
                  O processo inteiro: {comMilhar(cota.enviados)} caracteres em{" "}
                  {lidos.reduce((s, l) => s + l.paginas, 0)} páginas.
                </p>
              ) : (
                <>
                  <p className="mt-2 text-sm font-semibold text-alerta-tinta">
                    O processo é maior do que uma análise alcança.
                  </p>
                  {/* As PÁGINAS, contadas nos marcadores — não estimadas por
                      divisor. O processo real que Victor mediu em 02/09 tem 98
                      páginas e média de 1.771 caracteres por página; com o
                      divisor de 2.400 a tela dizia "cerca de 23 páginas ficam
                      de fora" onde o número real é 33. Um erro de 30% num
                      número que o perito usa para decidir o que reenviar. */}
                  <p className="mt-1 text-sm text-tinta">
                    Vão ser lidas {paginasEmTexto(cota.paginasLidas)} das{" "}
                    {paginasEmTexto(cota.paginasTotais)} do processo —{" "}
                    {comMilhar(cota.enviados)} de {comMilhar(cota.enviados + cota.deFora)}{" "}
                    caracteres
                    {cota.cotaPorArquivo
                      ? `, e cada arquivo entra com os primeiros ${comMilhar(cota.cotaPorArquivo)}`
                      : ""}
                    .
                  </p>
                  <p className="mt-2 text-xs text-cinza">
                    {/* O TETO em caracteres é exato; "cerca de N páginas" seria
                        estimativa tirada de UM documento, e declarar no código
                        que um número é estimado protege quem lê o CÓDIGO, não o
                        perito — para ele, "565 páginas" é um número. As páginas
                        que a tela mostra são as DESTE processo, contadas nos
                        marcadores, na linha acima. */}
                    Uma análise alcança {comMilhar(TETO_DE_TEXTO)} caracteres. Para um processo
                    maior, envie primeiro a petição inicial, a contestação e a decisão
                    saneadora — onde ficam o objeto e os quesitos —, e o resto numa análise
                    nova.
                  </p>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            disabled={!podeEnviar}
            onClick={() => void enviar()}
            className={podeEnviar ? "pill-cta" : "pill-desligada"}
          >
            {enviando === "criando"
              ? "Salvando o processo…"
              : enviando === "analisando"
                ? "A IA está lendo o processo…"
                : lendo
                  ? "Lendo os arquivos…"
                  : `Analisar ${lidos.length > 1 ? `os ${lidos.length} arquivos` : "o processo"}`}
          </button>

          {!podeEnviar && enviando === null && (
            <p className="text-xs text-cinza">
              {lidos.length === 0
                ? "Escolha ao menos um PDF com texto."
                : lendo
                  ? "Espere a leitura terminar."
                  : "Escolha o tipo de perícia."}
            </p>
          )}

          {erroDoEnvio && (
            <p className="rounded-cf border border-erro-borda bg-erro-fundo p-3 text-sm text-erro-tinta">
              {erroDoEnvio}
            </p>
          )}
        </div>
      </section>
    </>
  );
}
